import type {FilterValues, OrderByRequestBody} from '@dms/api/core';

import {getApiDateString} from 'shared/web';

import type {CustomerInsightsGridQueryArgs} from '../hooks/useCustomerInsightsGridQuery';
import {CUSTOMER_INSIGHTS_GRID_FIELDS} from './customerInsightsGridFields';
import {
  PURCHASE_BUSINESS_CASE_TYPES,
  SELLING_BUSINESS_CASE_TYPES,
} from './getCustomerInsightsBusinessCaseCategory';

type CustomerInsightsGridConfig = Pick<
  CustomerInsightsGridQueryArgs,
  'gridCode' | 'filters' | 'order' | 'columns' | 'rows' | 'includeTotal'
>;

export interface CustomerInsightsGridConfigs {
  openInterests: CustomerInsightsGridConfig;
  openBusinessCases: CustomerInsightsGridConfig;
  unsuccessfulInterests: CustomerInsightsGridConfig;
  unsuccessfulBusinessCases: CustomerInsightsGridConfig;
  pastSales: CustomerInsightsGridConfig;
  pastPurchases: CustomerInsightsGridConfig;
  openServiceCases: CustomerInsightsGridConfig;
  serviceHistory: CustomerInsightsGridConfig;
  overdueInvoices: CustomerInsightsGridConfig;
  overdueTasks: CustomerInsightsGridConfig;
}

const DESCENDING_UPDATED_AT_ORDER: OrderByRequestBody[] = [{columnKey: 'updatedAt', order: 'DESC'}];
const DESCENDING_BUSINESS_CASE_CLOSED_AT_ORDER: OrderByRequestBody[] = [
  {columnKey: 'businessCaseClosedAt', order: 'DESC'},
];
const ACTIVE_BUSINESS_CASE_STATES = [
  'CONCEPT',
  'OFFER',
  'CONTRACT',
  'DEPOSIT',
  'DEPOSIT_PAID',
  'INVOICE',
  'PAID',
];

interface BusinessCaseVisibility {
  businessCaseSales: boolean;
  businessCasePurchases: boolean;
}

const BUSINESS_CASE_COLUMNS = [
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.businessCase.code,
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.businessCase.state,
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.businessCase.type,
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.businessCase.vehicleTitle,
  ...[...CUSTOMER_INSIGHTS_GRID_FIELDS.businessCase.occurredAt].reverse(),
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.businessCase.owner,
];
const INTEREST_COLUMNS = [
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.interest.code,
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.interest.state,
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.interest.type,
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.interest.leadQuality,
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.interest.vehicleTitle,
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.interest.owner,
  ...[...CUSTOMER_INSIGHTS_GRID_FIELDS.interest.occurredAt].reverse(),
];
const SERVICE_CASE_COLUMNS = [
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.serviceCase.state,
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.serviceCase.code,
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.serviceCase.vehicleTitle,
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.serviceCase.subtitle,
  ...[...CUSTOMER_INSIGHTS_GRID_FIELDS.serviceCase.occurredAt].reverse(),
].filter((column, index, columns) => columns.indexOf(column) === index);
const SERVICE_HISTORY_COLUMNS = SERVICE_CASE_COLUMNS;
const INVOICE_COLUMNS = [
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.overdueInvoices.code,
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.overdueInvoices.amount,
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.overdueInvoices.occurredAt,
];
const TASK_COLUMNS = [
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.overdueTasks.code,
  ...CUSTOMER_INSIGHTS_GRID_FIELDS.overdueTasks.occurredAt,
];

const ALL_BUSINESS_CASES_VISIBLE: BusinessCaseVisibility = {
  businessCaseSales: true,
  businessCasePurchases: true,
};

const getVisibleBusinessCaseTypes = (visibility: BusinessCaseVisibility) => [
  ...(visibility.businessCaseSales ? SELLING_BUSINESS_CASE_TYPES : []),
  ...(visibility.businessCasePurchases ? PURCHASE_BUSINESS_CASE_TYPES : []),
];

export function getCustomerInsightsGridConfigs(
  customerId: string,
  todayApiDate = getApiDateString(new Date()),
  businessCaseVisibility: BusinessCaseVisibility = ALL_BUSINESS_CASES_VISIBLE
): CustomerInsightsGridConfigs {
  const openInterestsFilters: FilterValues = {
    customer: [customerId],
    state: ['NEW', 'OPEN'],
  };
  return {
    openInterests: {
      gridCode: 'customer-interests-list',
      filters: openInterestsFilters,
      order: DESCENDING_UPDATED_AT_ORDER,
      columns: INTEREST_COLUMNS,
      rows: 'firstFive',
      includeTotal: true,
    },
    openBusinessCases: {
      gridCode: 'business-case',
      filters: {
        customerName: [customerId],
        businessCaseState: ACTIVE_BUSINESS_CASE_STATES,
        businessCaseType: getVisibleBusinessCaseTypes(businessCaseVisibility),
      },
      order: [{columnKey: 'lastActivityDate', order: 'DESC'}],
      columns: BUSINESS_CASE_COLUMNS,
      rows: 'firstFive',
      includeTotal: true,
    },
    unsuccessfulInterests: {
      gridCode: 'customer-interests-list',
      filters: {customer: [customerId], state: ['UNSUCCESSFUL']},
      order: DESCENDING_UPDATED_AT_ORDER,
      columns: INTEREST_COLUMNS,
      rows: 'firstFive',
    },
    unsuccessfulBusinessCases: {
      gridCode: 'business-case',
      filters: {
        customerName: [customerId],
        businessCaseState: ['UNSUCCESSFUL'],
        businessCaseType: getVisibleBusinessCaseTypes(businessCaseVisibility),
      },
      order: DESCENDING_BUSINESS_CASE_CLOSED_AT_ORDER,
      columns: BUSINESS_CASE_COLUMNS,
      rows: 'firstFive',
    },
    pastSales: {
      gridCode: 'business-case',
      filters: {
        customerName: [customerId],
        businessCaseState: ['CLOSED'],
        businessCaseType: [...SELLING_BUSINESS_CASE_TYPES],
      },
      order: DESCENDING_BUSINESS_CASE_CLOSED_AT_ORDER,
      columns: BUSINESS_CASE_COLUMNS,
      rows: 'firstFive',
    },
    pastPurchases: {
      gridCode: 'business-case',
      filters: {
        customerName: [customerId],
        businessCaseState: ['CLOSED'],
        businessCaseType: [...PURCHASE_BUSINESS_CASE_TYPES],
      },
      order: DESCENDING_BUSINESS_CASE_CLOSED_AT_ORDER,
      columns: BUSINESS_CASE_COLUMNS,
      rows: 'firstFive',
    },
    openServiceCases: {
      gridCode: 'service-case-opensearch',
      filters: {
        customer: [customerId],
        state: ['DRAFT', 'PLANNED', 'CHECKED_IN', 'CREATED'],
      },
      order: DESCENDING_UPDATED_AT_ORDER,
      columns: SERVICE_CASE_COLUMNS,
      rows: 'firstFive',
      includeTotal: true,
    },
    serviceHistory: {
      gridCode: 'service-case-opensearch',
      filters: {customer: [customerId], state: ['HANDED_OVER', 'CANCELLED']},
      order: [{columnKey: 'handoverDate', order: 'DESC'}],
      columns: SERVICE_HISTORY_COLUMNS,
      rows: 'firstFive',
    },
    overdueInvoices: {
      gridCode: 'customer-invoicing',
      filters: {customer: [customerId], paymentState: ['overdue']},
      order: [{columnKey: 'dueDate', order: 'ASC'}],
      columns: INVOICE_COLUMNS,
      rows: 'firstOne',
    },
    overdueTasks: {
      gridCode: 'customer-task',
      filters: {
        relatedTo: customerId,
        state: ['TODO', 'IN_PROGRESS'],
        dueDate: {from: null, to: todayApiDate},
      },
      order: [{columnKey: 'dueDate', order: 'ASC'}],
      columns: TASK_COLUMNS,
      rows: 'firstOne',
    },
  };
}
